import { Component } from "react";
import { RouteComponentProps } from "react-router-dom";
import { appContext } from "../../../../AppContext";
import { Card, Grid, Divider, Typography, Box, Skeleton, CardContent } from "@mui/material";
import IbssButton from "../../../../Components/Buttons/Button/IbssButton";
import { StaticContext } from "react-router";
import { PagedResponse } from "../../../../Providers.Api/Models";
import IbssHorizontalTabs, { TabsParameters } from "../../../../Components/Layout/Tabs/IbssTabs";
import IbssDialog from "../../../../Components/Dialogs/BaseDialog/IbssDialog";
import IbssCheckBox from "../../../../Components/Inputs/CheckBox/IbssCheckBox";
import { Classification, Status } from "../../../../Providers.Api/CateringItems/CateringItemRepository";
import LoadingOverlay from "../../../../Components/Navigation/LoadingOverlay/LoadingOverlay";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import ChangeMenuPopup from "../../../Shared/CateringOrders/Edit/ChangeMenuPopup";
import { rights } from "../../../../Common/RightsManager";
import IbssCateringItemsAccordion from "../../../../Components/Miscellaneous/CateringItemsAccordion/IbssCateringItemsAccordion";
import IbssPageHeader from "../../../../Components/Headings/PageHeading/IbssPageHeader";
import "../../../../styles/css/dashboard.scss";
import "./cateringTemplate.scss";
import { getCurrencyByNodeId } from "../../../../Common/Helper";
import { ICateringMenuExpanded, ICateringTemplate, ICateringRestrictions, ICateringMenu, IMenuItemView } from "./DataModels";
import CateringSummaryPanel from "./CateringSummaryPanel";

export default class EditCateringTemplate extends IbssPage<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private get apiClient() { return appContext().apiClient; }
    private get localStorage() { return appContext().localStorageProvider; }
    private get appState() { return appContext().state; }
    private buildingId: number;
    private userCateringRights: boolean;
    private userCreateTemplateRights: boolean;
    private userUpdateTemplateRights: boolean;
    private userCateringRightsHas: boolean;

    constructor(props: IProps)
    {
        super(props);        
        this.state =
        {
            templateName: '',
            order: new CateringTemplateView(),
            orderItems: [],
            selectedMenuItemIds: [],
            loading: false,
            currency: '',
            menu: {
                menuId: '',
                name: ''
            },
            newMenuId: '',
            cateringRestrictions: [],
            selectedAllergenRestrictionsFilter: [],
            selectedAllergenRestrictionsFilterEdit: [],
            selectedDietaryRestrictionsFilterEdit: [],
            selectedDietaryRestrictionsFilter: [],
            showFilterModal: false,
            skeletonLoad: false,
            availableCateringMenusView: [],
            showChangeMenuPopup: false,
            availableCateringMenus: [],
            minimumBookingDuration: 0,
            updateMode: false,
            concurrencyStamp: '',
        };
        this.buildingId = parseInt(this.props.match.params.buildingid);
        this.userCreateTemplateRights = rights.DATAMODEL.CateringTemplates.Create.has();
        this.userUpdateTemplateRights = rights.DATAMODEL.CateringTemplates.Update.has();
        this.userCateringRights = rights.ONELENS360.OperationalServices.Catering.appliesTo().has(this.buildingId);
        this.userCateringRightsHas = rights.ONELENS360.OperationalServices.Catering.has();
    }

    public async componentDidMount(): Promise<void> 
    {
        const { match } = this.props;
        this.setState({ skeletonLoad: true });
        this.pageTitle = this.labels.funcCateringTemplate_S;

        try 
        {
            if(match.params.templateId)
            {
                await this.fetchCateringTemplate(match.params.templateId);
            }
            else 
            {
                await this.fetchAndSortCateringMenus();
            }
            await this.fetchAndSetCateringRestrictions();
            await this.getCurrency();        
        } catch (error) 
        {
        }
        finally 
        {
            this.setState({ skeletonLoad: false });
        }
    }

    private async fetchAndSortCateringMenus(menuId?: string): Promise<void> 
    {
        const cateringFilter = menuId ? `Status eq 'StatusActive' and Menu_Id eq '${menuId}'` : `Status eq 'StatusActive'`
        const cateringMenus = await appContext().ibssApiClientV2.v2.byNodeid.cateringMenu.get<PagedResponse<ICateringMenuExpanded[]>>
        ({
            nodeId: this.buildingId,
            recursive: true,
            top: 100,
            select: ICateringMenuExpanded,
            filter: cateringFilter
        });

        const sortedMenus = cateringMenus.value.slice().sort((a, b) => a.Name.toLowerCase().localeCompare(b.Name.toLowerCase()));
        this.setState({
            availableCateringMenusView: sortedMenus.map(menu => MenuView.fromApiModel(menu, null, this.userCateringRights)),
            availableCateringMenus: sortedMenus,
        });

        if (sortedMenus.length > 0) 
        {
            this.menuChanged(sortedMenus[0].Menu_Id);
        }
    }

    private async fetchCateringTemplate(templateId: string): Promise<void> 
    {
        const orderData: ICateringTemplate = await appContext().ibssApiClientV2.v2.byNodeid.cateringTemplates.byId.get<ICateringTemplate>
        ({
            nodeId: this.buildingId,
            id: templateId,
            select: ICateringTemplate,
        });   

        const order = CateringTemplateView.fromApiTask(orderData);
        await this.fetchAndSortCateringMenus(order.menuId);
        this.menuChanged(order.menuId);
        this.setState({
            updateMode: true,
            selectedMenuItemIds: order.selectedMenuItemIds,
            templateName: orderData.Name,
            minimumBookingDuration: orderData.MinimumBookingDuration,
            concurrencyStamp: orderData.ConcurrencyStamp,
        });
    }

    private async fetchAndSetCateringRestrictions(): Promise<void> 
    {
        const cateringRestrictions = await appContext().ibssApiClientV2.v2.byNodeid.cateringRestriction.get<PagedResponse<ICateringRestrictions[]>>
        ({
            nodeId: this.buildingId,
            select: ICateringRestrictions,
        });

        const filteredCateringRestrictions = cateringRestrictions.value.filter(x => x.Section === 'Allergen' || x.Section === 'Dietary');
        this.setState({
            cateringRestrictions: filteredCateringRestrictions.map(x => ({ id: x.Restriction_Id ?? '', section: x.Section, imageURI: x.ImageURI, name: x.Name })),
        });
    }

    private async getCurrency(): Promise<void> 
    {
        const currency  = getCurrencyByNodeId(this.buildingId);
        this.setState({ currency: currency });
    }    
    
    private handleItemCheckboxChange(itemId: string, checked: boolean): void
    {
        if (checked) 
        {
            if (!this.state.selectedMenuItemIds.includes(itemId)) 
            {
                this.setState({ 
                    selectedMenuItemIds: [...this.state.selectedMenuItemIds, itemId] 
                });
            }
        } else 
        {
            this.setState({ 
                selectedMenuItemIds: this.state.selectedMenuItemIds.filter(id => id !== itemId) 
            });
        }
    }

    private async submit(): Promise<void> 
    {
        this.setState({ loading: true });        
        try 
        {            
            const { menu, selectedMenuItemIds, minimumBookingDuration, templateName, concurrencyStamp, updateMode } = this.state;            
            
            const filteredItems = selectedMenuItemIds.filter(menuItemId => 
            {
                const item = this.state.orderItems.find(orderItem => orderItem.menuItemId === menuItemId);
                return item && item.status !== "StatusInactive";
            }).map(menuItemId => ({
                MenuItemId: menuItemId
            }));            
            
            const payload: IPayload = {
                Node_Id: this.buildingId,
                Menu_Id: menu.menuId,
                MenuItems: filteredItems,
                MinimumBookingDuration: minimumBookingDuration,
                Name: templateName,
                Description: templateName,
            };

            if (updateMode) 
            {
                payload.ConcurrencyStamp = concurrencyStamp;
            }

            let response: ICateringTemplate;
            if (updateMode) 
            {
                response = await appContext().ibssApiClientV2.v2.byNodeid.cateringTemplates.byId.put({
                    nodeId: this.buildingId,
                    id: this.props.match.params.templateId,
                    body: payload,
                });
            } else 
            {
                response = await appContext().ibssApiClientV2.v2.byNodeid.cateringTemplates.post({
                    nodeId: this.buildingId,
                    body: payload,
                });
            }

            if (response.MenuTemplate_Id) 
            {
                this.props.history.goBack();
            }        
        } 
        catch (error) 
        {
        }
        finally 
        {
            this.setState({ loading: false });
        }
    }



    private displayFilterModal(): void
    {
        this.setState({ showFilterModal: true, selectedAllergenRestrictionsFilterEdit: this.state.selectedAllergenRestrictionsFilter, selectedDietaryRestrictionsFilterEdit: this.state.selectedDietaryRestrictionsFilter })
    }

    private closeFilterModal(): void
    {
        this.setState({ showFilterModal: false, selectedAllergenRestrictionsFilterEdit: [], selectedDietaryRestrictionsFilterEdit: [] })
    }

    private applyRestrictionFilterUpdated(): void
    {        
        const updatedOrderItems = this.state.orderItems.map(x =>
        {            
            const itemDietaryRestrictions = x.menuItemRestrictions.filter(restriction => restriction.section == 'Dietary');
            const itemAllergenRestrictions = x.menuItemRestrictions.filter(restriction => restriction.section == 'Allergen');

            let dietaryFilterRequirementsValid = true;
            for (let index = 0; index < this.state.selectedDietaryRestrictionsFilterEdit.length; index++)
            {
                if (this.state.selectedDietaryRestrictionsFilterEdit.length > 0 && !itemDietaryRestrictions.map(x => x.name).includes(this.state.selectedDietaryRestrictionsFilterEdit[index]))
                {
                    dietaryFilterRequirementsValid = false;
                }
            }
            if (this.state.selectedDietaryRestrictionsFilterEdit.length > 0 && itemDietaryRestrictions.length == 0)
            {
                dietaryFilterRequirementsValid = false;
            }

            let allergenRequirementsValid = true;
            for (let index = 0; index < itemAllergenRestrictions.length; index++)
            {
                if (this.state.selectedAllergenRestrictionsFilterEdit.length > 0 && this.state.selectedAllergenRestrictionsFilterEdit.includes(itemAllergenRestrictions[index].name))
                {
                    allergenRequirementsValid = false;
                }
            }

            return (
                {
                    ...x,
                    meetsFilterRequirements: dietaryFilterRequirementsValid && allergenRequirementsValid
                }
            )
        });
        this.setState({
            showFilterModal: false,
            selectedAllergenRestrictionsFilter: this.state.selectedAllergenRestrictionsFilterEdit,
            selectedAllergenRestrictionsFilterEdit: [],
            selectedDietaryRestrictionsFilter: this.state.selectedDietaryRestrictionsFilterEdit,
            selectedDietaryRestrictionsFilterEdit: [],
            orderItems: updatedOrderItems
        });
    }

    private allergenFilterCheckboxSelected(checked: boolean, restriction: string): void
    {

        if (checked)
        {
            this.setState({ selectedAllergenRestrictionsFilterEdit: [...this.state.selectedAllergenRestrictionsFilterEdit, restriction] })
        }
        if (!checked)
        {
            const updatedItems = this.state.selectedAllergenRestrictionsFilterEdit.filter(x => x !== restriction)
            this.setState({ selectedAllergenRestrictionsFilterEdit: updatedItems })
        }
    }

    private dietaryFilterCheckboxSelected(checked: boolean, restriction: string): void
    {

        if (checked)
        {
            this.setState({ selectedDietaryRestrictionsFilterEdit: [...this.state.selectedDietaryRestrictionsFilterEdit, restriction] })
        }
        if (!checked)
        {
            const updatedItems = this.state.selectedDietaryRestrictionsFilterEdit.filter(x => x !== restriction)
            this.setState({ selectedDietaryRestrictionsFilterEdit: updatedItems })
        }
    }        
    
    private menuChanged(menuId?: string): void
    {        
        const selectedMenu = this.state.availableCateringMenusView.filter(x => x.menu.menuId == menuId);
        const orderItems = selectedMenu[0].orderItems.map(item => ({ ...item }));
        const cateringMenu = this.state.availableCateringMenus.filter(menu => menu.Menu_Id == menuId)[0];
        const menuView = MenuView.fromApiModel(cateringMenu, this.state.order, this.userCateringRights);

        this.setState({
            menu: selectedMenu[0].menu,
            orderItems: orderItems.map(x =>                
                {
                    const itemAvailable = menuView.orderItems.some(item => item.menuItemId == x.menuItemId && item.availableToOrder)
                    return (
                        { ...x, availableToOrder: itemAvailable }
                    )
                }
                ),
            selectedMenuItemIds: [],
            showChangeMenuPopup: false,
        });
    }    
    
    private selectMenu(menuId: string): void
    {
        if(this.state.selectedMenuItemIds.length > 0)
        {
            this.setState({ newMenuId: menuId, showChangeMenuPopup: true });
        }
        else
        {
            this.setState({ newMenuId: menuId });
            this.menuChanged(menuId);
        }
    }

    private createMenuOptionLabel(menu: MenuView): string
    {
        if(this.state.availableCateringMenusView.find(availableMenu => availableMenu.menu.menuId === menu.menu.menuId))
        {
            return menu.menu.name;
        }
        else
        {
            return `${menu.menu.name} (${this.labels.HubLabelUnavailable})`;
        }
    }

    private getMenusForDropDown()
    {
        const menus =  this.state.availableCateringMenusView.map(menu => ({ label: this.createMenuOptionLabel(menu), value: menu.menu.menuId })).sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
        return menus;
    }    
    
    private hanldeMinimumBookingDuration(value: number): void
    {
        this.setState({ 
            minimumBookingDuration: value
        });
    }

    private handleTemplateName(value: string): void
    {
       this.setState({
            templateName: value,
       })
    }

    private get availableMenuItemIds(): string[]
    {
        return this.state.availableCateringMenusView.flatMap(menu => menu.orderItems.map(orderItem => orderItem.menuItemId));
    }

    public render(): JSX.Element
    {
        const tabs: TabsParameters[] = [
            {
                label: this.labels.funcCateringOrderBeverages_S,
                components: (                    
                <IbssCateringItemsAccordion
                        classification="MenuItemClassificationBeverage"
                        order={this.state.order}
                        orderItems={this.state.orderItems}
                        selectedMenuItemIds={this.state.selectedMenuItemIds}
                        availableMenuItemIds={this.availableMenuItemIds}
                        userCateringRights={this.userCateringRightsHas}
                        currency={this.state.currency}
                        itemInputType="checkbox"
                        handleItemCheckboxChange={(menuItemId: string, checked: boolean) => this.handleItemCheckboxChange(menuItemId, checked)}
                    />
                )
            },
            {
                label: this.labels.funcCateringOrderSnacks_S,
                components: (                    
                <IbssCateringItemsAccordion
                        classification="MenuItemClassificationSnack"
                        order={this.state.order}
                        orderItems={this.state.orderItems}
                        selectedMenuItemIds={this.state.selectedMenuItemIds}
                        availableMenuItemIds={this.availableMenuItemIds}
                        userCateringRights={this.userCateringRightsHas}
                        currency={this.state.currency}
                        itemInputType="checkbox"
                        handleItemCheckboxChange={(menuItemId: string, checked: boolean) => this.handleItemCheckboxChange(menuItemId, checked)}
                    />
                )
            },
            {
                label: this.labels.funcCateringOrderFoods_S,
                components: (                    
                <IbssCateringItemsAccordion
                        classification="MenuItemClassificationFood"
                        order={this.state.order}
                        orderItems={this.state.orderItems}
                        selectedMenuItemIds={this.state.selectedMenuItemIds}
                        availableMenuItemIds={this.availableMenuItemIds}
                        userCateringRights={this.userCateringRightsHas}
                        currency={this.state.currency}
                        itemInputType="checkbox"
                        handleItemCheckboxChange={(menuItemId: string, checked: boolean) => this.handleItemCheckboxChange(menuItemId, checked)}
                    />
                )
            }
        ]; 

        const allergenRestrictions = this.state.cateringRestrictions.filter(x => x.section == 'Allergen');
        const dietaryRestrictions = this.state.cateringRestrictions.filter(x => x.section == 'Dietary');
        return (
            <>
                {this.state.loading && <LoadingOverlay />}
                <div className="page-height-exct-header">
                    <div className="rightPanel-main-content">
                    <ChangeMenuPopup
                        menuId={this.state.newMenuId}
                        open={this.state.showChangeMenuPopup}
                        onClose={() => this.setState({ showChangeMenuPopup: false })}
                        onCancelClicked={() => this.setState({ showChangeMenuPopup: false })}
                        onContinueClicked={() => this.menuChanged(this.state.newMenuId)}
                    />

                        <Card sx={{ padding: 1 }} className="mb-3">
                            <CardContent sx={{ p: '2' }}>
                                <Grid container spacing={2}>
                                    <Grid item md={12}>
                                        <IbssPageHeader 
                                            title={this.labels.funcAboutAutomatedCatering_L}
                                            description={this.labels.funcOrdersAllowYouToSetDefault_D}
                                        />
                                    </Grid>
                                    <Grid item md={5} xl={4}>                                        
                                        <CateringSummaryPanel
                                            skeletonLoad={this.state.skeletonLoad}
                                            orderItems={this.state.orderItems}
                                            selectedMenuItemIds={this.state.selectedMenuItemIds}
                                            currency={this.state.currency}
                                            availableMenus={this.getMenusForDropDown()}
                                            selectMenu={(menuId) => this.selectMenu(menuId)}
                                            selectedMenu={this.state.menu.menuId}
                                            minimumBookingDuration={this.state.minimumBookingDuration}
                                            hanldeMinimumBookingDuration={(value: number) => this.hanldeMinimumBookingDuration(value)}
                                            templateName={this.state.templateName}
                                            handleTemplateName={(value: string) => this.handleTemplateName(value)}
                                        />
                                    </Grid>
                                    <Grid item md={7} xl={8}>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                            <IbssPageHeader 
                                                title={this.labels.funcMenuSelection_S}
                                                description={this.labels.funcSelectItemsForAutomatedOrder_L}
                                            />
                                            <Box>
                                                <IbssButton variant="contained" onClick={() => this.displayFilterModal()}>{this.labels.funcFilterMenu_S}</IbssButton>
                                            </Box>
                                        </Box>
                                        {
                                            this.state.skeletonLoad ?
                                            <div>
                                                <Card sx={{ height: '124px', marginBottom: '10px', marginTop: '10px' }}><MenuItemSkeleton /></Card>
                                                <Card sx={{ height: '124px' }}><MenuItemSkeleton /></Card>
                                            </div> :
                                            this.state.menu.menuId == '' ?
                                                <Card>
                                                    <CardContent sx={{ textAlign: 'center', mb: "2" }}>
                                                        <img height={'auto'} width={'auto'} src={`/images/NoCateringMenuAvailable.svg`} alt="" />
                                                        <Typography className="mb-1" variant="h5">{this.labels.funcCateringOrderNoMenuAvailable_S}</Typography>
                                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                            <div style={{ width: '350px' }}>{this.labels.funcCateringOrderNoMenuAvailable_Message}</div>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                                :
                                                <Box>
                                                    <IbssHorizontalTabs
                                                        sx={{ backgroundColor: 'var(--ui-background-alternate)' }}
                                                        tabs={tabs}
                                                        orientation="horizontal"
                                                        boxwidth='100%'
                                                    />
                                                </Box>
                                        }
                                    </Grid>

                                    {/* -----------------------------Start Filter Popup---------------------------*/}
                                    <IbssDialog
                                        header={this.labels.funcCateringOrderFilterBy_S}
                                        open={this.state.showFilterModal}
                                        onClose={() => this.closeFilterModal()}
                                        dialogContent=
                                        {
                                            <>
                                                <div className="mb-3">{this.labels.funcCateringOrderFilter_Message}</div>
                                                <Divider />
                                                <div className="mb-2 mt-1">{this.labels.funcCateringOrderRemoveFoods_M}:</div>
                                                <div className="mb-3" style={{ display: 'flex', flexWrap: 'wrap' }}>
                                                    {allergenRestrictions.map(restriction => (<IbssCheckBox checked={this.state.selectedAllergenRestrictionsFilterEdit.includes(restriction.name)} label={restriction.name} onClicked={(e: { target: { checked: boolean } }) => this.allergenFilterCheckboxSelected(e.target.checked, restriction.name)} />))}
                                                </div>
                                                <Divider />
                                                <div className="mb-2 mt-1">{this.labels.funcCateringOrderOnlyShowMarkedFoods_M}:</div>
                                                <div className="mb-3" style={{ display: 'flex', flexWrap: 'wrap' }}>
                                                    {dietaryRestrictions.map(restriction => (<IbssCheckBox checked={this.state.selectedDietaryRestrictionsFilterEdit.includes(restriction.name)} label={restriction.name} onClicked={(e: { target: { checked: boolean } }) => this.dietaryFilterCheckboxSelected(e.target.checked, restriction.name)} />))}
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <IbssButton variant="contained" onClick={() => this.applyRestrictionFilterUpdated()}>{this.labels.funcCateringOrderApply_S}</IbssButton>
                                                </div>
                                            </>
                                        }
                                        fullWidth
                                    />
                                    {/* -----------------------------End Filter Popup---------------------------*/}
                                </Grid>
                            </CardContent>
                        </Card>
                    
                        <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', px: 3, pb: 2, gap: 2 }}>
                            <IbssButton
                                variant="contained"
                                color="secondary"
                                onClick={() => this.props.history.goBack()}>
                                {this.labels.HubButtonCancel}
                            </IbssButton>
                            <IbssButton
                                variant="contained"
                                onClick={() => this.submit()}
                                disabled={!this.userCreateTemplateRights || !this.userUpdateTemplateRights}
                            >
                                {this.state.updateMode ? this.labels.HubLabelUpdate : this.labels.HubButtonSave}
                            </IbssButton>
                        </Box>
                    </div>
                </div>
            </>
        );
    }

}

export class MenuItemSkeleton extends Component
{
    public render(): JSX.Element
    {
        return (
            <div style={{ display: 'flex', padding: '10px' }}>
                <Skeleton variant="rectangular" width={100} height={100} />
                <div style={{ width: '100%' }}>
                    <Skeleton sx={{ margin: '15px', marginBottom: '10px' }} variant="rectangular" width={'70%'} height={10} />
                    <Skeleton sx={{ margin: '15px' }} variant="rectangular" width={'50%'} height={10} />
                </div>
            </div>
        )
    }
}

export interface IProps extends RouteComponentProps<IQueryParams, StaticContext>
{
}

export interface IQueryParams
{
    buildingid: string;
    templateId: string;
}

export interface IState
{
    updateMode: boolean;
    templateName: string;
    order: CateringTemplateView;
    orderItems: CateringTemplateMenuItemView[];
    selectedMenuItemIds: string[];
    loading: boolean;
    currency: string;
    menu: ICateringMenuView;
    newMenuId: string;
    cateringRestrictions: ICateringRestrictionView[];
    selectedAllergenRestrictionsFilter: string[],
    selectedAllergenRestrictionsFilterEdit: string[],
    selectedDietaryRestrictionsFilter: string[],
    selectedDietaryRestrictionsFilterEdit: string[],
    showFilterModal: boolean,
    skeletonLoad: boolean,
    availableCateringMenusView: MenuView[],
    showChangeMenuPopup: boolean,
    availableCateringMenus: ICateringMenu[],
    minimumBookingDuration: number;
    concurrencyStamp: string;
}

export class CateringTemplateView
{
    public templateId = "";
    public nodeId = 0;
    public selectedMenuItemIds: string[] = [];
    public cateringOrderRestrictions = [];
    public menuId = "";

    constructor(value?: Partial<CateringTemplateView>)
    {
        if (value == null)
        {
            return;
        }
        Object.assign(this, value);
    }
      public static fromApiTask(data: ICateringTemplate): CateringTemplateView
    {
        let menuItemIds: string[] = [];
          try 
        {            
            if (Array.isArray(data.MenuItems))
            {
                menuItemIds = data.MenuItems.map((item: IMenuItemView) => item.MenuItemId);
            }
        
        } catch (error) 
        {
            menuItemIds = [];
        }

        return new CateringTemplateView(
            {
                templateId: data.MenuTemplate_Id,
                nodeId: data.Node_Id,
                selectedMenuItemIds: menuItemIds,
                menuId: data.Menu_Id
            });
    }

}



export class MenuView
{
    public menu = {
        menuId: '',
        name: ''
    };
    public orderItems: CateringTemplateMenuItemView[] = [];

    public static fromApiModel(cateringMenu: ICateringMenu, order: CateringTemplateView | null, userHasCateringRights: boolean): MenuView
    {
        return {
            menu: {
                menuId: cateringMenu.Menu_Id,
                name: cateringMenu.Name,
            },            
            orderItems: cateringMenu.Menu_MenuItems.map(menuItem =>
            {
                const availableToOrder = userHasCateringRights ? true : false;                
                
                return ({
                    description: menuItem.MenuItem.Description,
                    status: menuItem.MenuItem.Status,
                    unitPrice: menuItem.MenuItem.UnitPrice,
                    classification: menuItem.MenuItem.Classification,
                    retailPrice: menuItem.MenuItem.RetailPrice,
                    name: menuItem.MenuItem.Name,
                    supplierId: menuItem.MenuItem.Supplier_Id,
                    menuItemId: menuItem.MenuItem_Id,
                    nutritionalInformation: menuItem.MenuItem.NutritionalInformation,
                    imageURI: menuItem.MenuItem.ImageURI,
                    meetsFilterRequirements: true,
                    availableToOrder: availableToOrder,
                    showMenuItem: true,
                    menuItemRestrictions: menuItem.MenuItem.MenuItem_Restrictions.map(x => ({
                        id: x.Restriction_Id || '',
                        section: x.Restriction.Section,
                        imageURI: x.Restriction.ImageURI,
                        name: x.Restriction.Name
                    }))
                })
            }),
        };
    }
}

export interface ICostAllocation
{
    costCode: string;
    costCodeId: string;
    allocation: number;
    costCodeDescription: string;
}

export interface IGridTaskDetailValues
{
    details: string;
    original: string;
    change: string;
}


export interface CateringTemplateMenuItemView
{
    description: string;
    status: Status;
    unitPrice: number;
    classification: Classification;
    retailPrice: number;
    name: string;
    supplierId: string;
    menuItemId: string;
    nutritionalInformation: string;
    imageURI: string;
    availableToOrder: boolean;
    menuItemRestrictions: ICateringRestrictionView[];
    meetsFilterRequirements: boolean;
    showMenuItem: boolean;
}

export interface ICateringRestrictionView
{
    id: string;
    section: string;
    imageURI: string;
    name: string;
}

export interface ICateringMenuView
{
    menuId: string;
    name: string;
}

export interface IPayload
{
    Node_Id: number;
    Menu_Id: string;
    MenuItems: { MenuItemId: string; }[];
    MinimumBookingDuration: number;
    Name: string;
    Description: string;
    ConcurrencyStamp?: string; 
}